import { and, eq, type InitialQueryBuilder, type Ref } from "@tanstack/db";

import type { WsFeedGroup } from "@storyboard/web-client";
import { store } from "@/store/store";
import type { FeedGroup } from "@/store/types";

function transformWsFeedGroup(r: WsFeedGroup): FeedGroup {
  return {
    id: r.id,
    createdAt: r.createdAt ?? null,
    updatedAt: r.updatedAt ?? null,
    workspaceId: r.workspaceId,
    name: r.name,
    accountId: null,
    enabled: r.enabled ? 1 : 0,
  };
}

export async function bulkUpsertFeedGroup(records: WsFeedGroup[]) {
  if (records.length === 0) return;
  // The old ON CONFLICT SET replaced everything except accountId.
  store.upsert("feedGroup", records.map(transformWsFeedGroup), (existing, next) => ({
    ...next,
    accountId: existing.accountId,
  }));
}

/** Groups with at least one feed the membership can read (SQL EXISTS via join + distinct). */
export const groupsQuery = (workspaceId: string, myCurrentWorkspaceMembershipId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ group: store.collection("feedGroup") })
    .innerJoin({ groupMembership: store.collection("feedGroupMembership") }, ({ group, groupMembership }) =>
      eq(group.id, groupMembership.groupId),
    )
    .innerJoin({ permission: store.collection("permission") }, ({ groupMembership, permission }) =>
      eq(groupMembership.feedId, permission.feedId),
    )
    .where(({ group, permission }) =>
      and(
        eq(group.workspaceId, workspaceId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
        eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
      ),
    )
    // TanStack DB 0.12 types a whole-source select as `T | { [K]?: never }`; the annotation keeps the row type `T`.
    .select(({ group }): Ref<FeedGroup> => group)
    .distinct()
    .orderBy(({ group }) => group.name, { direction: "asc", nulls: "last" });
